import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';
import { ExpandMore } from '@material-ui/icons';
import React, { useState, useEffect } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import OptionSelect from './optionSelect';
import getNextPrompt, {
  PromptObj,
  getUnsupportedOptions,
  createGeneratorOptions,
  getArchitecturalMessage,
  shouldCreateNewJourney,
  shouldUploadPolicy,
} from './dialogOptions';
import { testGenerateXML } from '../policyUploadUI';
import { getJsonEditor, getLastPolicies, getSessionId, setJSONEditor, setLastPolicies } from '../../../store';
import { PolicyRecord } from '../../../view-models';
import { useDispatch, useSelector } from 'react-redux';
import PolicyUploadUIModalComponent, { DeploymentSettingsField, PolicyUploadUIComponent } from '../policyUploadUI';
import { useFeedbackButton } from '../userFeedback';
import { getTenantSettings } from '../../../jsonParsing/utils';
import settings from '../../../constants/jsonXMLsettings';
import { Typography } from '@material-ui/core';
import Box from '@mui/material/Box';
import jsonConstants from '../../../constants/jsonConstants';

interface StartWizardProps {
  show: boolean; //if the popup should be shown
  onCancel: Function; // if the user cancels before completion
  onComplete: Function; // if the user finishes the wizard and a new JSON should be generated (this should accept an object with )
  onUpload: Function; // if the user decides to edit an existing policy
  onBuild: Function; // Function to build the template policy right now, without closing the wizard
  deployState: string; // The state of policy deployment
  setDeployState: Function; // Function to set the deployment UI state
  deploymentEnv: string;
  setDeploymentEnv: Function;
  enableJavascript: boolean;
  setEnableJavascript: Function;
  journeyInsightsSettings: any;
  setJourneyInsightsSettings: Function;
}

// function getSelectionIds(selection: boolean[], prompt: PromptObj) {
//   const selectedOptions = prompt.options.filter((opt, index) => selection[index]);
//   const selectedOptionIds = selectedOptions.map((opt) => opt.id);
//   if (selectedOptionIds) {
//     return selectedOptionIds;
//   }
//   return [];
// }

function getAllSelections(previousPrompts: PromptObj[]) {
  /**
   * Gets all the option ids from the all previous prompts passed and their selected options
   */
  let allSelections: string[] = [];
  previousPrompts.forEach((prevPrompt) => {
    allSelections = [...allSelections, ...prevPrompt.defaultSelection];
  });
  return allSelections;
}

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" sx={{ height: 10, borderRadius: 5 }} {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export default function StartWizard({
  show,
  onCancel,
  onComplete,
  onUpload,
  onBuild,
  deployState,
  setDeployState,
  deploymentEnv,
  setDeploymentEnv,
  enableJavascript,
  setEnableJavascript,
  journeyInsightsSettings,
  setJourneyInsightsSettings
}: StartWizardProps) {
  const classes = jsonEditorStyles();
  const [previousPrompts, setPreviousPrompts] = useState<PromptObj[]>([]);
  const [wizardCompleted, setCompleted] = useState<boolean>(false);
  const [deploymentTenantId, setDeploymentTenantId] = useState<string>('');
  const [generatedTemplate, setGeneratedTemplate] = useState<any>({});
  const [showWizardDeployment, setShowWizardDeployment] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [prompt, setPrompt] = useState({
    singleSelect: true,
    defaultSelection: [],
    prompt: '',
    percentage: 0,
    options: [{ id: '', displayName: '', disabled: true, branch: [] }],
  });
  const { feedbackShown, Feedback, FeedbackButton } = useFeedbackButton([{ key: 'feedback', label: 'Feedback' }]);

  useEffect(() => {
    const nextPrompt: any = getNextPrompt(getAllSelections(previousPrompts));
    setPrompt(nextPrompt);
  }, []);
  const onNextClick = () => {
    //handles the click of the next button
    //filter the options select
    if (prompt.defaultSelection.length === 0) {
      return;
    } // makes sure the user has selected at least one option
    previousPrompts.push(prompt);
    setPreviousPrompts(previousPrompts);
    //log user selections for log rocket collection
    //add current prompt to the throw awa
    console.log('User has clicked the next button the following are their current selected scenarios:');
    const nextPrompt = getNextPrompt(getAllSelections(previousPrompts));
    if (nextPrompt) setPrompt(nextPrompt);
    else setCompleted(true);
  };

  const onBackClick = () => {
    const newPrevPrompts = [...previousPrompts];
    const newPrompt = newPrevPrompts.pop();
    if (newPrompt !== undefined) {
      setPreviousPrompts(newPrevPrompts);
      // @ts-ignore
      setPrompt(newPrompt);
    }
  };

  const onCloseButtonClick = (deploymentEnv: string, sessionID?: any, lastPolicies?: any, dispatch?: any) => {
    const allSelections = getAllSelections(previousPrompts);
    if (shouldUploadPolicy(allSelections)) {
      setTimeout(onUpload(), 100);
    }

    if (shouldCreateNewJourney(allSelections)) {
      const genOptions = createGeneratorOptions(allSelections);
      const edState = onBuild(genOptions);
      setGeneratedTemplate(edState);
      setDeploymentTenantId(
        edState.templatization.environments[deploymentEnv].settings[jsonConstants.defaultAppSettingFields.Tenant.id],
      );

      setDeployState('uploading');
      testGenerateXML(
        edState,
        sessionID!,
        lastPolicies!,
        () => {},
        setDeployState,
        (npn: PolicyRecord[]) => {
          dispatch(setLastPolicies(npn));
        },
        deploymentEnv,
        {
          enableJavascript: settings.instrumentPolicyHTMLWithLR
            ? settings.instrumentPolicyHTMLWithLR
            : enableJavascript,
          enableJourneyInsights: journeyInsightsSettings.enableJourneyInsights
        },
      );
      setShowWizardDeployment(true);
    } else {
      onCancel();
    }
  };

  const SelectionHTML = () => {
    return (
      <div>
        <DialogContent>
          <h4>{prompt.prompt}</h4>
          <OptionSelect
            singleSelect={prompt.singleSelect}
            options={prompt.options}
            onChangeOptions={(selected: string[]) => {
              // @ts-ignore
              setPrompt({ ...prompt, defaultSelection: selected });
            }}
            defaultSelection={prompt.defaultSelection}
          />
        </DialogContent>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={show && !feedbackShown}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <DialogTitle>Quickstart Wizard</DialogTitle>
        <div style={{ padding: '0 20px 0 20px' }}>
          This quick start wizard will help us set you up with a base flowchart that you can customize. This is a
          limited sandbox. To get help with customizing please contact us at{' '}
          <b>
            <a href="mailto:info@gritsoftwaresystems.com" target="_blank">
              info@gritsoftwaresystems.com
            </a>
          </b>
        </div>
        {showWizardDeployment ? (
          <>
            <Box sx={{ padding: '20px 40px 0 20px', width: '80%' }}>
              <LinearProgressWithLabel value={100} />
            </Box>
            <WizardDeploymentScreen
              userSelection={getAllSelections(previousPrompts)}
              onExitButtonClick={(gs: any) => {
                dispatch(setJSONEditor(gs));
              }}
              deployState={deployState}
              templateState={generatedTemplate}
              feedbackButton={<FeedbackButton />}
              deploymentTenantId={deploymentTenantId}
            />
          </>
        ) : wizardCompleted ? (
          <>
            <Box sx={{ padding: '20px 40px 0 20px', width: '80%' }}>
              <LinearProgressWithLabel value={95} />
            </Box>
            <WizardCompletionScreen
              onCancelButtonClick={onCancel}
              onContinueButtonClick={onCloseButtonClick}
              userSelection={getAllSelections(previousPrompts)}
              feedbackButton={<FeedbackButton />}
              deploymentEnv={deploymentEnv}
              setDeploymentEnv={setDeploymentEnv}
              enableJavascript={enableJavascript}
              setEnableJavascript={setEnableJavascript}
              journeyInsightsSettings={journeyInsightsSettings}
              setJourneyInsightsSettings={setJourneyInsightsSettings}
            />
          </>
        ) : (
          <div>
            <Box sx={{ padding: '20px 40px 0 20px', width: '80%' }}>
              <LinearProgressWithLabel value={prompt.percentage || 49} />
            </Box>
            {SelectionHTML()}
            <DialogActions>
              <FeedbackButton />
              <Button onClick={(e) => onCancel()}>Cancel</Button>
              {previousPrompts.length > 0 ? <Button onClick={() => onBackClick()}>Back</Button> : null}
              <Button onClick={(e) => onNextClick()}>Next</Button>
            </DialogActions>
          </div>
        )}
      </Dialog>
      <Feedback />
    </>
  );
}

interface UserSelectionProps {
  userSelection: string[];
}

function NotImplementedText({ userSelection }: UserSelectionProps) {
  const unsupportedOptions = getUnsupportedOptions(userSelection);
  if (unsupportedOptions.length === 0) {
    return null;
  }
  const unsupportedHTML = unsupportedOptions.map((unsupportedOption, index) => {
    return (
      <li key={index}>
        {unsupportedOption.prompt}: {unsupportedOption.optionName}
      </li>
    );
  });
  return (
    <div>
      <h4>Unfortunately, we do not yet support the following options with this template generation wizard.</h4>
      <ul>{unsupportedHTML}</ul>
    </div>
  );
}

function useMissingScenariosBox() {
  const [text, setText] = useState('');
  const onTextChange = (e: any) => {
    const newText: string = e.target.value;
    // DO some text cleaning maybe
    setText(newText);
  };

  return {
    textBox: <MissedScenariosInput text={text} onChange={onTextChange} />,
    text: text,
  };
}

interface MissingScenariosProps {
  /**
   * Props for using the missing scenarios text box
   */
  text: string; // text of the box
  onChange: (event: any) => void; // defines the behavior if the user tries to change the box's text
}

function MissedScenariosInput({ text, onChange }: MissingScenariosProps) {
  const label = <Typography>Are there any scenarios we missed in this wizard that you would like to see?</Typography>;
  const inputLabel = <Typography>Missing scenarios</Typography>;
  return (
    <div>
      {label}
      <TextField
        label={inputLabel}
        fullWidth
        multiline
        sx={{
          mb: 1,
        }}
        value={text}
        onChange={onChange}
      ></TextField>
    </div>
  );
}

function ArchitecturalConsiderations({ userSelection }: UserSelectionProps) {
  const messages = getArchitecturalMessage(userSelection);
  if (messages.length === 0) {
    return null;
  }
  const messageHTML = messages.map((message) => <li>{message}</li>);
  return (
    <Accordion sx={{ color: 'text.secondary' }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography>Some architectural considerations with your journeys:</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <ul>{messageHTML}</ul>
      </AccordionDetails>
    </Accordion>
  );
}

interface CompletionScreenProps {
  userSelection: string[];
  onContinueButtonClick: Function;
  onCancelButtonClick: Function;
  feedbackButton: any;
  deploymentEnv: string;
  setDeploymentEnv: Function;
  enableJavascript: boolean;
  setEnableJavascript: Function;
  journeyInsightsSettings: any;
  setJourneyInsightsSettings: Function;
}

function WizardCompletionScreen({
  userSelection,
  onContinueButtonClick,
  onCancelButtonClick,
  feedbackButton,
  deploymentEnv,
  setDeploymentEnv,
  enableJavascript,
  setEnableJavascript,
  journeyInsightsSettings,
  setJourneyInsightsSettings
}: CompletionScreenProps) {
  /**
   * Displays final message to the user before they are taken to main application
   */
  const sessionID = useSelector(getSessionId);
  const lastPolicies = useSelector(getLastPolicies);
  const dispatch = useDispatch();
  const goingToXMLEditor = shouldUploadPolicy(userSelection);
  const { text, textBox } = useMissingScenariosBox();

  return (
    <DialogContent>
      <DialogContentText>
        <h3>
          {goingToXMLEditor
            ? 'You have completed the quickstart wizard. Select continue below to go to the IEF policy XML editor.'
            : 'You have completed the quickstart wizard. Select continue below to build, deploy and test journeys reflecting your selections.'}
        </h3>
        {textBox}
        <h4>
          If you have any questions about Grit's services or Microsoft B2C, join our free{' '}
          <a
            href="https://join.slack.com/t/gritsoftwaresystemshq/shared_invite/zt-1kvxj8vao-wVuVE1oUyhcez2EY~UcZrA"
            target="_blank"
            rel="noreferrer noopener"
          >
            slack channel.
          </a>
          <br />
          It's a vibrant community of developers with years of experience developing B2C tools. All we ask is you share
          your knowledge too!
        </h4>
        <NotImplementedText userSelection={userSelection} />
        <ArchitecturalConsiderations userSelection={userSelection} />
        <DeploymentSettingsField
          deploymentEnv={deploymentEnv}
          setDeploymentEnv={setDeploymentEnv}
          enableJavascript={
            settings.instrumentPolicyHTMLWithLR ? settings.instrumentPolicyHTMLWithLR : enableJavascript
          }
          setEnableJavascript={setEnableJavascript}
          lockJavascriptField={settings.instrumentPolicyHTMLWithLR}
          journeyInsightsSettings={journeyInsightsSettings}
          setJourneyInsightsSettings={setJourneyInsightsSettings}
          containerStyle={{ my: 2 }}
          style={{ my: 2, color: 'text.secondary' }}
        />
      </DialogContentText>
      <DialogActions>
        {feedbackButton}
        <Button onClick={(e) => onCancelButtonClick()}>Cancel</Button>
        <Button
          onClick={(e) => {
            console.log(`Missing scenarios:`);
            console.log(text);
            onContinueButtonClick(deploymentEnv, sessionID, lastPolicies, dispatch);
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </DialogContent>
  );
}

interface DeploymentScreenProps {
  userSelection: string[];
  onExitButtonClick: Function;
  deployState: string;
  templateState: any;
  feedbackButton: any;
  deploymentTenantId: string;
}

interface PolicyTestStep {
  instructionText: string;
  link?: string;
  linkText?: string;
  conditions: string[];
}

const policyTestSteps: PolicyTestStep[] = [
  { instructionText: 'Click the "Test Authentication Journey" link below to run the journey.', conditions: [] },
  {
    instructionText:
      'The first time you run the sign-in flow, select the "Create New Account" button and go through the account creation process to create a user to test with.',
    conditions: ['selfServiceSignUp'],
  },
  {
    instructionText:
      'The first time you run the sign-in flow, select the "Create New Account" button and go through the account creation process to create a user to test with. (Note that self-service signup is enabled in your journey specifically for testing purposes.)',
    conditions: ['invitedSignUp'],
  },
  {
    instructionText:
      'If your device supports biometric authentication, you will be asked to enable it at the end of sign-up. Do so to allow testing biometric authentication later',
    conditions: ['biometrics'],
  },
  { instructionText: 'Click the "Test Authentication Journey" link again.', conditions: ['emailPwdSocial'] },
  {
    instructionText:
      'If you enabled biometrics, you will be prompted with a menu to select an identity. Cancel this prompt.',
    conditions: ['emailPwdSocial', 'biometrics'],
  },
  {
    instructionText:
      'Sign in using the email and password you created the user with. Make sure you receive the auth token at the end of the flow.',
    conditions: ['emailPwdSocial'],
  },
  { instructionText: 'Click the "Test Authentication Journey" link again.', conditions: ['otp'] },
  {
    instructionText:
      'If you enabled biometrics, you will be prompted with a menu to select an identity. Cancel this prompt.',
    conditions: ['otp', 'biometrics'],
  },
  {
    instructionText:
      'Select the "OTP" button. Sign in using a passwordless code sent to your phone or email. Make sure you receive the auth token at the end of the flow.',
    conditions: ['otp'],
  },
  { instructionText: 'Click the "Test Authentication Journey" link again.', conditions: ['biometrics'] },
  {
    instructionText:
      'When prompted with an identity selection menu for biometric sign-in, select the user you created previously and enabled biometrics for. Make sure you receive the auth token at the end of the flow.',
    conditions: ['biometrics'],
  },
  {
    instructionText:
      'For migration you will first need to create existing users. Please use the following link to create a SHA-256 password. You can use any value for the salt, such as "1234", but keep track of the salt used.',
    conditions: ['migrateIdP', 'migratePassword', 'sha256'],
    link: 'https://hashgeneratoruserbuilder20221012181057.azurewebsites.net/',
    linkText: 'Hash Generator Link',
  },
  {
    instructionText:
      'After creating the password, go to the following link and enter the hashed password and salt to create the existing user:',
    conditions: ['migrateIdP', 'migratePassword', 'sha256'],
    link: 'https://hashgeneratoruserbuilder20221012181057.azurewebsites.net/Home/UserBuilder',
    linkText: 'User Builder Link',
  },
  {
    instructionText:
      'Next, click the "Test Authentication Journey" link and you will be able to sign in as the created user, via the username and (plaintext) password.',
    conditions: ['migrateIdP', 'migratePassword', 'sha256'],
  },
  {
    instructionText:
      'For migration you will first need to create existing users. Please use the following link to create preexisting users to test with. Use any arbitrary values for password and salt, but keep track of the password used.',
    conditions: ['migrateIdP', 'forceResetPassword'],
    link: 'https://hashgeneratoruserbuilder20221012181057.azurewebsites.net/Home/UserBuilder',
    linkText: 'User Builder Link',
  },
  {
    instructionText:
      'Next, click the "Test Authentication Journey" link and you can sign in as the created user, with the entered password from the previous step. You will then be asked to reset the password.',
    conditions: ['migrateIdP', 'forceResetPassword'],
  },
];

function getPolicyTestSteps(userSelection: any) {
  let instructions = policyTestSteps
    .filter((pts: PolicyTestStep) => {
      return pts.conditions.every((cond: string) => userSelection.includes(cond));
    })
    .map((pts: PolicyTestStep, idx: number) => {
      if (pts.link) {
        return (
          <li key={idx}>
            {pts.instructionText}{' '}
            <a href={pts.link} target="_blank" rel="noreferrer noopener">
              {pts.linkText}
            </a>
          </li>
        );
      } else {
        return <li key={idx}>{pts.instructionText}</li>;
      }
    });
  return <ol>{instructions}</ol>;
}

function WizardDeploymentScreen({
  userSelection,
  onExitButtonClick,
  deployState,
  templateState,
  feedbackButton,
  deploymentTenantId,
}: DeploymentScreenProps) {
  return (
    <DialogContent>
      <DialogContentText>
        <h3>Test Instructions</h3>
        {getPolicyTestSteps(userSelection)}
      </DialogContentText>
      <PolicyUploadUIComponent
        deployState={deployState}
        showPolicyName={false}
        deploymentTenantId={deploymentTenantId}
      />
      <DialogActions>
        {feedbackButton}
        <Button
          onClick={(e) => {
            onExitButtonClick(templateState);
          }}
        >
          Exit (View in flowchart editor)
        </Button>
      </DialogActions>
    </DialogContent>
  );
}
